import { PreparedItem, PreparedWorkItem } from '@/models/item.models';
import {
  Initiative,
  Milestone,
  RankEntity,
  Relationship,
  Roadmap,
} from '@/api';

// ------------------------------------ Request payloads ------------------------------------ //

/**
 * @transient
 *
 * Payload for updating the following attributes of Gantt items:
 *  - Initiatives
 *    - rankBelow
 *  - Milestones
 *    - rankBelow
 *    - dueDate
 *    - relationshipsAdded
 *    - relationshipsRemoved
 *  - Workstreams or work items
 *    - rankBelow
 *    - startDate
 *    - dueDate
 *    - relationshipsAdded
 *    - relationshipsRemoved
 *  - Work items
 *    - boardId
 */
export interface UpdateGanttItem {
  startDate?: string;
  dueDate?: string;
  /**
   * rank of the item BELOW which the current item should be placed.
   */
  rankBelow?: PreparedItem['number'] | Milestone['id'] | Initiative['id'];
  /**
   * rank of the item ABOVE which the current item should be placed.
   * This won't always be present. `rankBelow` should be the source of truth.
   */
  rankAbove?: PreparedItem['number'] | Milestone['id'] | Initiative['id'];

  /** only needed by backend for initiative/milestone based ranking. name of the entity to which rankBelow belongs to */
  rankEntity?: RankEntity;
  useWhichRank?: 'rankBelow' | 'rankAbove';

  relationshipsAdded?: Array<{
    entityId: PreparedItem['number'] | Milestone['id'];
    relationType: Relationship['type'];
  }>;
  relationshipsRemoved?: Array<PreparedItem['number'] | Milestone['id']>;

  boardId?: PreparedWorkItem['boardId'] | null;
}

// ------------------------------------ Entities ------------------------------------ //

/** @transient */
export enum GanttItemType {
  INITIATIVE = 'initiative',
  MILESTONE = 'milestone',
}

/** @transient */
export type OptimisticUpdateReturnType = {
  [
    key:
      | PreparedItem['number']
      | PreparedGanttMilestone['id']
      | PreparedGanttInitiative['id']
  ]: {
    orderUpdated: boolean;
    startDateUpdated: boolean;
    dueDateUpdated: boolean;
    relationshipsUpdated: boolean;
    boardIdUpdated: boolean;
  };
};

// ------------------------------------ Prepared entities ------------------------------------ //

/** @transient */
export interface PreparedGanttInitiative extends Initiative {
  itemType: GanttItemType.INITIATIVE;
  inferredStartDate: string;
  inferredDueDate: string;
}

/** @transient */
export interface PreparedGanttMilestone extends Milestone {
  itemType: GanttItemType.MILESTONE;
}

/** @transient */
export interface PreparedGanttRoadmap extends Roadmap {
  initiatives: PreparedGanttInitiative[];
  milestones: PreparedGanttMilestone[];
  /**
   * List of all FIRST LEVEL children of all the initiatives in the chart.
   *
   * **Plus all the children of workstreams that have been expanded.**
   *
   * This should also update (shallow copy) the `parentRank` of level 1 items based on `roadmapItems`
   * so the whole thing is usable out of the box by the gantt logic
   */
  items: PreparedItem[];
  originalDates: {
    [
      identifier:
        | PreparedGanttInitiative['id']
        | PreparedItem['number']
        | PreparedGanttMilestone['id']
    ]: {
      startDate: string | undefined;
      dueDate: string | undefined;
    };
  };
  optimisticUpdatesMeta: OptimisticUpdateReturnType;
}
